import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { Badge, badgeVariants } from "../components/ui/badge"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"
import { resolveIcon } from "./resolve-icon"
import { positionToken } from "../lib/design-tokens"
import { resolveColor } from "../lib/style-resolver"

export function BadgeRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, hasEventBinding, itemContext, mode } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("badge")
  const resolvedVariantRaw = element.variant ?? atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : ""
  const ariaLabel = element.ariaLabel
    ? String(resolveSymbol(element.ariaLabel) ?? "")
    : undefined
  const rawDisabled = element.disabled
  const isDisabled = typeof rawDisabled === "string"
    ? Boolean(resolveSymbol(rawDisabled))
    : Boolean(rawDisabled)

  const variantMap: Record<string, "default" | "secondary" | "destructive" | "outline" | "none"> = {
    default: "default",
    secondary: "secondary",
    destructive: "destructive",
    outline: "outline",
    none: "none",
  }

  // Props-first: trust Normalizer-set variant. No className-based inference —
  // EXCEPT the authored-surface escape: with no explicit variant and an
  // authored bg-* utility, the variant color pair must yield ("none"), because
  // the unlayered .cn-badge-variant-* rules beat authored utilities in the
  // cascade (tinted chips like `bg-primary/10 text-primary` otherwise render
  // as solid primary-on-primary).
  const authoredSurface = element.variant == null && /(^|\s)bg-/.test(element.className ?? "")
  const variant = authoredSurface ? "none" : (variantMap[resolvedVariantRaw] ?? "default")

  // Resolve icon (same pattern as button-renderer)
  const rawIcon = element.icon ?? ""
  const iconName = rawIcon.includes("#{")
    ? String(resolveSymbol(rawIcon) ?? "")
    : rawIcon
  const IconComponent = iconName ? resolveIcon(iconName) : undefined

  // Resolve color via centralized resolver
  const { className: colorClass } = resolveColor(element.color as string | undefined)

  // Position classes (for absolute-positioned badges like cart badge)
  const posClasses: string[] = []
  if (element.position?.type === "absolute") {
    posClasses.push("absolute")
    if (element.position.top) {
      const v = positionToken(element.position.top)
      posClasses.push(v.startsWith("-") ? `-top-${v.slice(1)}` : `top-${v}`)
    }
    if (element.position.right) {
      const v = positionToken(element.position.right)
      posClasses.push(v.startsWith("-") ? `-right-${v.slice(1)}` : `right-${v}`)
    }
    if (element.position.left) {
      const v = positionToken(element.position.left)
      posClasses.push(v.startsWith("-") ? `-left-${v.slice(1)}` : `left-${v}`)
    }
    if (element.position.bottom) {
      const v = positionToken(element.position.bottom)
      posClasses.push(v.startsWith("-") ? `-bottom-${v.slice(1)}` : `bottom-${v}`)
    }
  }

  const sizeClasses: Record<string, string> = {
    xs: "w-5 rounded-full p-0 text-[10px]",
    sm: "gap-1 px-1.5 py-0 text-[10px]",
    default: "",
    md: "",
    lg: "gap-1.5 px-3 py-1.5 text-sm",
  }

  const className = cn(...posClasses, sizeClasses[resolvedSize], colorClass, stripPresetConflicts(element.className, undefined, element.designAuthorityClassName)) || undefined

  const iconEl = IconComponent
    ? <IconComponent className={element.iconClassName ?? "h-3.5 w-3.5 mr-1.5"} />
    : null

  const hasTapBinding = Boolean(element.events?.tap)
    || hasEventBinding?.(element.id, "tap") === true

  if (hasTapBinding) {
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (mode === "edit") event.preventDefault()
      if (mode !== "edit") event.stopPropagation()
      dispatchEvent(element.id, "tap", itemContext)
    }

    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label={ariaLabel}
        disabled={isDisabled}
        className={cn(badgeVariants({ variant }), className)}
        data-value={text || undefined}
        data-action="action"
        data-atom-type="badge"
        data-variant={resolvedVariantRaw}
        data-size={resolvedSize}
      >
        {iconEl}
        {text}
      </button>
    )
  }

  return (
    <Badge variant={variant} className={className} data-value={text || undefined} data-atom-type="badge" data-variant={resolvedVariantRaw} data-size={resolvedSize}>
      {iconEl}
      {text}
    </Badge>
  )
}
